import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import type {
  ApplicationTestingActivity,
  ApplicationTestingAttachmentInfo,
  ApplicationTestingCommentAttachmentInfo,
  ApplicationTestingTicketDetail,
} from '@/core/models/applicationTesting.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { applicationTestingService } from '@/core/services/applicationTesting';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { Select } from '@/kit/Select';
import { ALLOWED_EXTENSIONS, appendValidatedFiles, filesFromClipboard } from './applicationTestingFiles';
import { isRichTextEmpty, RichTextView, ticketEditorConfig } from './applicationTestingText';
import { seedServerTags, subscribeToTags, tagsVersion, ticketTags as tagsOf } from './applicationTestingTags';
import { ApplicationTestingTimeline } from './ApplicationTestingTimeline';
import {
  priorityOptions,
  renderPriorityLabel,
  renderStatusLabel,
  statusChoicesFor,
  TagPicker,
} from './ApplicationTestingTickets';
import { STATUS_PANEL_MIN_WIDTH } from './applicationTestingStatus';
import { viewImage } from './ApplicationTestingImageViewer';
import './SupportTicket.scss';
import './ApplicationTestingTicketDetails.scss';

const FILE_TYPE_LEGEND = [
  { icon: 'image', label: 'Image', variant: 'image' },
  { icon: 'picture_as_pdf', label: 'PDF', variant: 'pdf' },
  { icon: 'description', label: 'DOC/DOCX', variant: 'doc' },
  { icon: 'grid_on', label: 'XLS/XLSX', variant: 'xls' },
  { icon: 'table_rows', label: 'CSV', variant: 'csv' },
];

const MAX_SUBJECT_LENGTH = 255;
const DESCRIPTION_EDITOR = ticketEditorConfig('Describe the problem…', '160');
const COMMENT_EDITOR = ticketEditorConfig('Add a comment…', '150');

function fileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return 'picture_as_pdf';
  if (contentType.startsWith('image/')) return 'image';
  return 'insert_drive_file';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentLike {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/**
 * One attachment, shared by the ticket's own attachments and comment attachments (different endpoints, passed in
 * as `fetchImage`/`download`). An image attachment lazily fetches and shows a real thumbnail; clicking it shows
 * the full image in the image viewer. Other file types get an icon and download on click. `variant` keeps the two kinds
 * visually distinct: "tile" is the ticket's own square attachment grid, "chip" is a compact pill embedded inside
 * a comment bubble, next to the WhatsApp-style message it belongs to.
 */
function AttachmentItem({
  attachment,
  fetchImage,
  download,
  variant,
}: {
  attachment: AttachmentLike;
  fetchImage: (id: string) => Promise<Blob>;
  download: (id: string) => Promise<void>;
  variant: 'tile' | 'chip';
}) {
  const isImage = attachment.contentType.startsWith('image/');
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  // An object URL (not a large data: string held in state), revoked when the attachment goes. The blob is re-typed
  // from the attachment's content type, since the API may serve it as application/octet-stream.
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    fetchImage(attachment.id).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: attachment.contentType }));
        setThumbnail(objectUrl);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once per attachment
  }, [attachment.id]);

  const open = () => {
    if (isImage && thumbnail) {
      viewImage(thumbnail, attachment.fileName);
      return;
    }
    download(attachment.id).catch((error) => notifyError(error));
  };

  const prefix = variant === 'tile' ? 'application-testing-attachment' : 'application-testing-comment-attachment';

  return (
    <button
      type="button"
      className={`${prefix}${isImage && thumbnail ? ` ${prefix}--image` : ''}`}
      title={attachment.fileName}
      onClick={open}
    >
      {isImage && thumbnail ? (
        <img src={thumbnail} alt={attachment.fileName} className={`${prefix}-image`} />
      ) : (
        <Icon name={fileIcon(attachment.contentType)} outlined />
      )}
      <span className={`${prefix}-name`}>{attachment.fileName}</span>
      {variant === 'tile' && <span className="font-size-12">{formatSize(attachment.sizeBytes)}</span>}
    </button>
  );
}

function TicketAttachment({ attachment }: { attachment: ApplicationTestingAttachmentInfo }) {
  return (
    <AttachmentItem
      attachment={attachment}
      fetchImage={applicationTestingService.getAttachment}
      download={applicationTestingService.downloadAttachment}
      variant="tile"
    />
  );
}

function CommentAttachment({ attachment }: { attachment: ApplicationTestingCommentAttachmentInfo }) {
  return (
    <AttachmentItem
      attachment={attachment}
      fetchImage={applicationTestingService.getCommentAttachment}
      download={applicationTestingService.downloadCommentAttachment}
      variant="chip"
    />
  );
}

/**
 * A file waiting to go with the next comment, shown under the comment box: an image as a small preview (so a
 * pasted screenshot is visibly there), anything else as an icon; either way with its name and a remove button.
 */
function StagedFile({ file, onRemove }: { file: File; onRemove: () => void }) {
  const isImage = file.type.startsWith('image/');
  const [preview, setPreview] = useState<string | null>(null);

  // A data: URL is fine here - the preview is only shown on this page, never opened in a new tab.
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    const reader = new FileReader();
    reader.onload = () => {
      if (!cancelled && typeof reader.result === 'string') setPreview(reader.result);
    };
    reader.readAsDataURL(file);
    return () => {
      cancelled = true;
    };
  }, [file, isImage]);

  return (
    <span className={`application-testing-file-chip${preview ? ' application-testing-file-chip--image' : ''}`}>
      {preview ? (
        <button
          type="button"
          className="application-testing-file-chip-view"
          aria-label={`View ${file.name}`}
          onClick={() => viewImage(preview, file.name)}
        >
          <img src={preview} alt={file.name} className="application-testing-file-chip-image" />
        </button>
      ) : (
        <Icon name={fileIcon(file.type)} outlined />
      )}
      <span className="application-testing-file-chip-name" title={file.name}>
        {file.name}
      </span>
      <a
        href="#"
        aria-label={`Remove ${file.name}`}
        onClick={(event) => {
          event.preventDefault();
          onRemove();
        }}
      >
        <Icon name="close" />
      </a>
    </span>
  );
}

/** Full detail of one Application Testing ticket: a full page, opened by clicking a row in the list. */
export function ApplicationTestingTicketDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ApplicationTestingTicketDetail | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [isCommenting, setIsCommenting] = useState(false);
  const commenting = useRef(false);
  const [editingField, setEditingField] = useState<'subject' | 'description' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [isSavingField, setIsSavingField] = useState(false);
  const [isAttaching, setIsAttaching] = useState(false);
  const [activity, setActivity] = useState<ApplicationTestingActivity[] | null>(null);

  /** The ticket's history. A failure leaves the last list as it was: the history is secondary to the ticket itself. */
  const loadActivity = useCallback(() => {
    if (!id) return;
    applicationTestingService.getActivity(id).then(
      (rows) => setActivity(Array.isArray(rows) ? rows : []),
      () => setActivity((current) => current ?? []),
    );
  }, [id]);

  const loadDetail = useCallback(() => {
    if (!id) return;
    applicationTestingService.getDetail(id).then(
      // Defensive: tolerates an API that has not deployed the comments field yet.
      (loaded) => {
        seedServerTags([loaded]);
        setDetail({ ...loaded, comments: loaded.comments ?? [] });
        loadActivity();
      },
      (error) => {
        notifyError(error);
        navigate('/home/application-testing');
      },
    );
  }, [id, navigate, loadActivity]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const goBack = () => navigate('/home/application-testing');

  /** The ticket's sample tags, as the list shows them (from its subject and description until tags are stored). */
  useSyncExternalStore(subscribeToTags, tagsVersion);
  const ticketTags = detail ? tagsOf(detail) : [];

  const updateStatus = (status: number) => {
    if (!id || !detail || detail.statusId === status || isSaving) return;
    setIsSaving(true);
    applicationTestingService.setStatus(id, status).then(
      (saved) => {
        setIsSaving(false);
        setDetail((prev) => (prev ? { ...prev, status: saved.status, statusId: saved.statusId } : prev));
        notifySuccess(`${saved.ticketNo} is now ${saved.status}`);
        loadActivity();
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  const updatePriority = (priority: number) => {
    if (!id || !detail || detail.priorityId === priority || isSaving) return;
    setIsSaving(true);
    applicationTestingService.setPriority(id, priority).then(
      (saved) => {
        setIsSaving(false);
        setDetail((prev) => (prev ? { ...prev, priority: saved.priority, priorityId: saved.priorityId } : prev));
        notifySuccess(`${saved.ticketNo} priority set to ${saved.priority}`);
        loadActivity();
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  const startEditingField = (field: 'subject' | 'description') => {
    if (!detail || isSavingField) return;
    setFieldDraft(field === 'subject' ? detail.subject : detail.description);
    setEditingField(field);
  };

  const cancelEditingField = () => {
    setEditingField(null);
    setFieldDraft('');
  };

  /** Subject and description are saved together (the server's `update` writes both columns every time), so
   *  whichever field isn't being edited is sent back unchanged. */
  const saveEditingField = () => {
    if (!id || !detail || !editingField || isSavingField) return;
    const value = editingField === 'subject' ? fieldDraft.trim() : fieldDraft;
    if (editingField === 'subject' ? value.length === 0 : isRichTextEmpty(value)) {
      notifyError(editingField === 'subject' ? 'Enter the subject.' : 'Enter the description.');
      return;
    }
    if (editingField === 'subject' && value.length > MAX_SUBJECT_LENGTH) {
      notifyError(`The subject must be ${MAX_SUBJECT_LENGTH} characters or fewer.`);
      return;
    }

    const subject = editingField === 'subject' ? value : detail.subject;
    const description = editingField === 'description' ? value : detail.description;

    setIsSavingField(true);
    applicationTestingService.updateTicket(id, subject, description).then(
      (saved) => {
        setIsSavingField(false);
        setDetail((prev) => (prev ? { ...prev, subject: saved.subject, description } : prev));
        setEditingField(null);
        notifySuccess('Changes saved.');
      },
      (error) => {
        setIsSavingField(false);
        notifyError(error);
      },
    );
  };

  /** Ctrl+V anywhere on the ticket card (not just while editing Subject/Description - the card itself is a paste
   *  target via tabIndex) attaches a pasted screenshot or copied file to the ticket directly, no separate upload
   *  step; files that fail the type/size checks are reported and left out. A paste
   *  that started inside the comment composer is handled there instead (it stops the event reaching here), so
   *  one paste never attaches to both the ticket and a comment at once. */
  /** Uploads files straight onto the ticket and shows them in its Attachments (paste, the file picker, a drop). */
  const attachToTicket = (files: File[]) => {
    if (!id || isAttaching || files.length === 0) return;
    setIsAttaching(true);
    applicationTestingService.addTicketAttachments(id, files).then(
      () => {
        setIsAttaching(false);
        loadDetail();
        notifySuccess(files.length > 1 ? 'Files attached.' : 'File attached.');
      },
      (error) => {
        setIsAttaching(false);
        notifyError(error);
      },
    );
  };

  const onTicketPaste = (event: ClipboardEvent<HTMLElement>) => {
    if (!id || isAttaching) return;
    const pasted = filesFromClipboard(event);
    if (pasted.length === 0) return;
    event.preventDefault();
    attachToTicket(appendValidatedFiles([], pasted));
  };

  /** The Attachment panel's picker (and a file dropped on it) attaches straight to the ticket, like a paste does;
   *  files that fail the type/size checks are reported and left out. */
  const onAttachmentFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const incoming = Array.from(input.files ?? []);
    input.value = '';
    attachToTicket(appendValidatedFiles([], incoming));
  };

  const removeCommentFile = (index: number) => setCommentFiles((current) => current.filter((_, i) => i !== index));

  /** Ctrl+V in the composer attaches a pasted screenshot or copied file the same way the file picker does, staged like
   *  any other comment attachment until the comment is actually sent. Stops the event so it doesn't also reach
   *  the ticket-level paste handler on the surrounding card (one paste, one destination). */
  const onCommentPaste = (event: ClipboardEvent<HTMLElement>) => {
    const pasted = filesFromClipboard(event);
    if (pasted.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    setCommentFiles((current) => appendValidatedFiles(current, pasted));
  };

  const submitComment = () => {
    if (!id || commenting.current) return;
    if (isRichTextEmpty(commentText)) {
      // The API requires text on every comment, attachments or not; say so when only a file is waiting.
      notifyError(commentFiles.length > 0 ? 'Add a note to go with the attachment.' : 'Enter a comment.');
      return;
    }
    commenting.current = true;
    setIsCommenting(true);
    applicationTestingService
      .addComment(id, commentText, commentFiles)
      .then(
        () => {
          setCommentText('');
          setCommentFiles([]);
          loadDetail();
        },
        (error) => notifyError(error),
      )
      .finally(() => {
        commenting.current = false;
        setIsCommenting(false);
      });
  };

  /** A comment can run to several paragraphs, so Enter starts a new line; Ctrl/Cmd+Enter sends it. */
  const onComposerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submitComment();
    }
  };

  return (
    <div className="form ticket-wrapper support-ticket-page application-testing-page">
      <div className="application-testing-page-inner">
        <div className="application-testing-back" onClick={goBack}>
          <Icon name="arrow_back" />
          <span className="link">Back to Application Testing</span>
        </div>

        {!detail && <h2 className="m-0">Application Testing</h2>}

        {detail && (
          // tabIndex makes the card itself a focus target: native Ctrl+V only fires on whatever currently has
          // focus, and a plain div can't receive focus (or therefore a paste event) without one. Clicking
          // anywhere in the card that isn't a more specific focusable control (an input, a button, ...) focuses
          // the card, so paste works card-wide, not just inside the Subject/Description edit fields.
          <div className="application-testing-detail-card" tabIndex={-1} onPaste={onTicketPaste}>
            {/* Header: the ticket's identity, then Status / Priority / Created on, each set off by a divider. */}
            <div className="application-testing-header">
              <div className="application-testing-identity">
                <span className="application-testing-header-icon">
                  <Icon name="description" outlined />
                </span>
                <h2 className="m-0">{detail.ticketNo}</h2>
                <span className="application-testing-badge">Application</span>
              </div>
              <div className="application-testing-meta">
                <p className="application-testing-field-label">
                  <Icon name="schedule" outlined />
                  Status
                </p>
                <Select
                  className="application-testing-meta-select"
                  items={statusChoicesFor(detail.statusId)}
                  bindLabel="name"
                  bindValue="id"
                  value={detail.statusId}
                  clearable={false}
                  disabled={isSaving}
                  onChange={(value) => updateStatus(value as number)}
                  openOnFocus
                  panelClass="myPanelClass application-testing-status-panel"
                  panelMinWidth={STATUS_PANEL_MIN_WIDTH}
                  renderLabel={renderStatusLabel}
                  renderOption={renderStatusLabel}
                />
              </div>
              <div className="application-testing-meta">
                <p className="application-testing-field-label">
                  <Icon name="flag" outlined />
                  Priority
                </p>
                <Select
                  className="application-testing-meta-select"
                  items={priorityOptions}
                  bindLabel="name"
                  bindValue="id"
                  value={detail.priorityId}
                  clearable={false}
                  disabled={isSaving}
                  onChange={(value) => updatePriority(value as number)}
                  openOnFocus
                  panelClass="myPanelClass"
                  renderLabel={renderPriorityLabel}
                  renderOption={renderPriorityLabel}
                />
              </div>
              <div className="application-testing-meta">
                <p className="application-testing-field-label">
                  <Icon name="calendar_today" outlined />
                  Created on
                </p>
                <p className="application-testing-created">{detail.date}</p>
              </div>
              <div className="application-testing-meta application-testing-meta-tags">
                <p className="application-testing-field-label">
                  <Icon name="sell" outlined />
                  Tags
                </p>
                <TagPicker ticketId={detail.id} tags={ticketTags} onSaved={loadActivity} />
              </div>
            </div>

            <div className="application-testing-body">
              <div className="application-testing-section">
                <div className="application-testing-section-title">
                  <p className="application-testing-field-label">
                    <Icon name="person_outline" outlined />
                    Subject
                  </p>
                  {editingField !== 'subject' && (
                    <Icon
                      name="edit"
                      outlined
                      className="cursor-pointer text-gray-200"
                      title="Edit subject"
                      onClick={() => startEditingField('subject')}
                    />
                  )}
                </div>
                {editingField === 'subject' ? (
                  <div className="d-flex align-items-center gap-10">
                    <textarea
                      rows={2}
                      className="application-testing-field-box application-testing-field-input"
                      value={fieldDraft}
                      autoFocus
                      disabled={isSavingField}
                      maxLength={MAX_SUBJECT_LENGTH}
                      onChange={(event) => setFieldDraft(event.target.value.replace(/\s*[\r\n]+\s*/g, ' '))}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          saveEditingField();
                        } else if (event.key === 'Escape') {
                          cancelEditingField();
                        }
                      }}
                    />
                    <Icon
                      name="check"
                      className="cursor-pointer text-success"
                      title="Save"
                      onClick={saveEditingField}
                    />
                    <Icon name="close" className="cursor-pointer" title="Cancel" onClick={cancelEditingField} />
                  </div>
                ) : (
                  <p className="application-testing-field-box">{detail.subject}</p>
                )}
              </div>

              <div className="application-testing-section">
                <div className="application-testing-section-title">
                  <p className="application-testing-field-label">
                    <Icon name="notes" outlined />
                    Description
                  </p>
                  {editingField !== 'description' && (
                    <Icon
                      name="edit"
                      outlined
                      className="cursor-pointer text-gray-200"
                      title="Edit description"
                      onClick={() => startEditingField('description')}
                    />
                  )}
                </div>
                {editingField === 'description' ? (
                  <div>
                    <div
                      className="application-testing-editor"
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') cancelEditingField();
                      }}
                    >
                      <RichTextEditor
                        id="applicationTestingDescriptionEdit"
                        value={fieldDraft}
                        config={{ ...DESCRIPTION_EDITOR, editable: !isSavingField }}
                        onChange={setFieldDraft}
                      />
                    </div>
                    <div className="d-flex gap-10 mt-5">
                      <Icon
                        name="check"
                        className="cursor-pointer text-success"
                        title="Save"
                        onClick={saveEditingField}
                      />
                      <Icon name="close" className="cursor-pointer" title="Cancel" onClick={cancelEditingField} />
                    </div>
                  </div>
                ) : (
                  <RichTextView value={detail.description} className="application-testing-field-box" />
                )}
              </div>

              {detail.attachments.length > 0 && (
                <div className="application-testing-section">
                  <div className="application-testing-section-title">
                    <p className="application-testing-field-label">
                      <Icon name="attach_file" outlined />
                      Attachments
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-10">
                    {detail.attachments.map((attachment) => (
                      <TicketAttachment key={attachment.id} attachment={attachment} />
                    ))}
                  </div>
                </div>
              )}

              {/* The comment thread and the attachment for the next comment, side by side. */}
              <div className="application-testing-panels">
                <div className="application-testing-panel">
                  <div className="application-testing-panel-header">
                    <Icon name="chat_bubble_outline" outlined />
                    <span>Comments</span>
                    <span className="application-testing-panel-count">({detail.comments.length})</span>
                  </div>
                  <div className="application-testing-panel-body">
                    {detail.comments.length === 0 ? (
                      <p className="m-0">No comments yet.</p>
                    ) : (
                      <div className="application-testing-comments">
                        {detail.comments.map((comment) => (
                          <div key={comment.id} className="application-testing-comment-bubble">
                            <div className="d-flex align-items-center justify-space-between gap-10">
                              <span className="fw-bold">{comment.authorName}</span>
                              <span className="text-gray font-size-12">{comment.date}</span>
                            </div>
                            {comment.text && (
                              <RichTextView value={comment.text} className="application-testing-comment-text" />
                            )}
                            {comment.attachments.length > 0 && (
                              <div className="application-testing-comment-attachments">
                                {comment.attachments.map((attachment) => (
                                  <CommentAttachment key={attachment.id} attachment={attachment} />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="application-testing-composer">
                      <div
                        className="application-testing-composer-input"
                        onKeyDown={onComposerKeyDown}
                        onPaste={onCommentPaste}
                      >
                        <RichTextEditor
                          id="applicationTestingComment"
                          value={commentText}
                          config={{ ...COMMENT_EDITOR, editable: !isCommenting }}
                          onChange={setCommentText}
                        />
                      </div>
                      <button
                        type="button"
                        className="application-testing-composer-send"
                        aria-label="Send comment"
                        disabled={isCommenting}
                        onClick={submitComment}
                      >
                        <Icon name="send" />
                      </button>
                    </div>

                    {/* What was pasted into the comment box, right under it, until the comment is sent. */}
                    {commentFiles.length > 0 && (
                      <div className="application-testing-selected-files">
                        {commentFiles.map((file, index) => (
                          <StagedFile
                            key={`${file.name}-${file.size}-${index}`}
                            file={file}
                            onRemove={() => removeCommentFile(index)}
                          />
                        ))}
                      </div>
                    )}

                    <div className="application-testing-comment-actions">
                      <Button
                        className="action-button primary-button application-testing-add-comment"
                        disabled={isCommenting}
                        onClick={submitComment}
                      >
                        <Icon name="add" />
                        Add Comment
                      </Button>
                      {/* Pasting into the comment box attaches the image/file to the comment (onCommentPaste);
                          nothing else on the panel says so. */}
                      <span className="application-testing-paste-hint">
                        <Icon name="content_paste" outlined />
                        e.g. paste a screenshot or file into the comment box <kbd>Ctrl</kbd>+<kbd>V</kbd>
                        &middot; <kbd>Ctrl</kbd>+<kbd>Enter</kbd> sends
                      </span>
                    </div>
                  </div>
                </div>

                <div className="application-testing-panel">
                  <div className="application-testing-panel-header">
                    <Icon name="attach_file" outlined />
                    <span>Attachment</span>
                  </div>
                  <div className="application-testing-panel-body">
                    <div className="application-testing-dropzone">
                      <Icon name="cloud_upload" outlined />
                      <input
                        multiple
                        type="file"
                        autoComplete="off"
                        id="application-testing-comment-file"
                        accept={ALLOWED_EXTENSIONS.join(',')}
                        disabled={isAttaching}
                        onChange={onAttachmentFilesSelected}
                        className="cursor-pointer"
                      />
                      <label htmlFor="application-testing-comment-file" className="cursor-pointer">
                        <strong>Choose a file</strong>, drag it here or paste it (Ctrl+V) to attach it to this ticket.
                        <span className="d-block">Max file size: 2 MB</span>
                      </label>
                    </div>
                    <div className="application-testing-filetype-legend">
                      {FILE_TYPE_LEGEND.map((type) => (
                        <div key={type.label} className={`application-testing-filetype-badge ${type.variant}`}>
                          <Icon name={type.icon} outlined />
                          <span>{type.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="application-testing-panel">
                  <div className="application-testing-panel-header">
                    <Icon name="history" outlined />
                    <span>Activity</span>
                  </div>
                  <div className="application-testing-panel-body">
                    <ApplicationTestingTimeline activity={activity} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
